const OPEN_CLASS = 'is-open';
const BODY_OPEN_CLASS = 'modal-open';
const VIEW_SURVEY_CLASS = 'view-survey';
const VIEW_FORM_CLASS = 'view-form';
const HAS_BACK_CLASS = 'has-back';
const ACTIVE_CLASS = 'is-active';
const LAST_STEP_CLASS = 'is-last-step';
const ERROR_VISIBLE_CLASS = 'is-visible';

const STEP_SELECTOR = '.contact-survey-step-1, .contact-survey-step-2, .contact-survey-step-3, .contact-survey-step-4';

const STEP_VALIDATORS = [
	(step) => !!step.querySelector('.js-industries .js-option.' + ACTIVE_CLASS),
	(step) => !!step.querySelector('.js-applications .js-option.' + ACTIVE_CLASS),
	(step) => {
		const inputs = step.querySelectorAll('.js-position input, .js-organization input');
		return Array.from(inputs).every((input) => input.value.trim() !== '');
	},
	() => true,
];

const getSteps = (survey) => Array.from(survey.querySelectorAll(STEP_SELECTOR));

const getCurrentStepIndex = (survey) => {
	const index = getSteps(survey).findIndex((step) => step.classList.contains(ACTIVE_CLASS));
	return index === -1 ? 0 : index;
};

const updateControls = (survey, stepIndex, totalSteps) => {
	const controls = survey.querySelector('.js-contact-survey-controls');
	if (!controls) return;

	const progress = controls.querySelector('.js-progress');
	const currentEl = controls.querySelector('.js-current-step');
	const totalEl = controls.querySelector('.js-total-step');
	const prevBtn = controls.querySelector('.js-prev-button');

	if (progress) progress.style.width = `${((stepIndex + 1) / totalSteps) * 100}%`;
	if (currentEl) currentEl.textContent = stepIndex + 1;
	if (totalEl) totalEl.textContent = totalSteps;
	if (prevBtn) prevBtn.style.visibility = stepIndex === 0 ? 'hidden' : 'visible';

	controls.classList.toggle(LAST_STEP_CLASS, stepIndex === totalSteps - 1);
};

const goToStep = (survey, index) => {
	const steps = getSteps(survey);
	if (!steps.length) return;
	const clamped = Math.max(0, Math.min(index, steps.length - 1));
	steps.forEach((step, i) => step.classList.toggle(ACTIVE_CLASS, i === clamped));
	updateControls(survey, clamped, steps.length);
};

const validateStep = (survey, index) => {
	const step = getSteps(survey)[index];
	if (!step) return true;
	const validator = STEP_VALIDATORS[index];
	const isValid = validator ? validator(step) : true;
	const errorEl = step.querySelector('.js-error-message');
	if (errorEl) errorEl.classList.toggle(ERROR_VISIBLE_CLASS, !isValid);
	return isValid;
};

const handleOptionSelect = (option) => {
	const list = option.closest('.option-list');
	if (!list) return;
	list.querySelectorAll('.js-option').forEach((btn) => btn.classList.remove(ACTIVE_CLASS));
	option.classList.add(ACTIVE_CLASS);

	const step = option.closest(STEP_SELECTOR);
	const errorEl = step && step.querySelector('.js-error-message');
	if (errorEl) errorEl.classList.remove(ERROR_VISIBLE_CLASS);
};

const showView = (modal, view) => {
	const section = modal.querySelector('.js-contact-request-section');
	const header = modal.querySelector('.js-contact-request-header');
	if (!section) return;

	section.classList.remove(VIEW_SURVEY_CLASS, VIEW_FORM_CLASS);
	if (view === 'survey') section.classList.add(VIEW_SURVEY_CLASS);
	if (view === 'form') section.classList.add(VIEW_FORM_CLASS);

	if (header) header.classList.toggle(HAS_BACK_CLASS, view !== 'hub');
};

const resetModal = (modal) => {
	showView(modal, 'hub');
	const survey = modal.querySelector('.js-contact-survey');
	if (survey) goToStep(survey, 0);
};

const openModal = (modal) => {
	modal.classList.add(OPEN_CLASS);
	document.body.classList.add(BODY_OPEN_CLASS);
};

const closeModal = (modal) => {
	modal.classList.remove(OPEN_CLASS);
	document.body.classList.remove(BODY_OPEN_CLASS);
	resetModal(modal);
};

const updateFilledState = (wrap) => {
	const input = wrap.querySelector('.text-input');
	if (input) wrap.classList.toggle('filled', input.value.trim() !== '');
};

export const initModal = () => {
	if (!document.querySelector('.js-open-modal-button')) return;

	document.querySelectorAll('.js-contact-survey').forEach((survey) => goToStep(survey, 0));
	document.querySelectorAll('.text-input-wrap').forEach(updateFilledState);

	document.addEventListener('focusin', (event) => {
		const wrap = event.target.closest('.text-input-wrap');
		if (wrap) wrap.classList.add('focused');
	});

	document.addEventListener('focusout', (event) => {
		const wrap = event.target.closest('.text-input-wrap');
		if (wrap) {
			wrap.classList.remove('focused');
			updateFilledState(wrap);
		}
	});

	document.addEventListener('input', (event) => {
		const wrap = event.target.closest('.text-input-wrap');
		if (wrap) updateFilledState(wrap);
	});

	document.addEventListener('click', (event) => {
		const openButton = event.target.closest('.js-open-modal-button');
		if (openButton) {
			const modal = document.getElementById(openButton.dataset.modalId);
			if (modal) openModal(modal);
			return;
		}

		const closeButton = event.target.closest('.js-close-modal-button');
		if (closeButton) {
			const modal = closeButton.closest('.js-modal');
			if (modal) closeModal(modal);
			return;
		}

		const backButton = event.target.closest('.js-back-button');
		if (backButton) {
			const modal = backButton.closest('.js-modal');
			if (modal) showView(modal, 'hub');
			return;
		}

		const surveyButton = event.target.closest('.js-open-survey-button');
		if (surveyButton) {
			const modal = surveyButton.closest('.js-modal');
			if (modal) {
				showView(modal, 'survey');
				const survey = modal.querySelector('.js-contact-survey');
				if (survey) goToStep(survey, 0);
			}
			return;
		}

		const formButton = event.target.closest('.js-open-form-button');
		if (formButton) {
			const modal = formButton.closest('.js-modal');
			if (modal) showView(modal, 'form');
			return;
		}

		const option = event.target.closest('.js-option');
		if (option) {
			handleOptionSelect(option);
			return;
		}

		const nextButton = event.target.closest('.js-next-button');
		if (nextButton) {
			const survey = nextButton.closest('.js-contact-survey');
			if (survey) {
				const current = getCurrentStepIndex(survey);
				if (validateStep(survey, current)) {
					goToStep(survey, current + 1);
				}
			}
			return;
		}

		const prevButton = event.target.closest('.js-prev-button');
		if (prevButton) {
			const survey = prevButton.closest('.js-contact-survey');
			if (survey) goToStep(survey, getCurrentStepIndex(survey) - 1);
			return;
		}

		const openModalEl = event.target.closest('.js-modal');
		if (openModalEl && !event.target.closest('.modal-content')) {
			closeModal(openModalEl);
		}
	});

	document.addEventListener('keydown', (event) => {
		if (event.key !== 'Escape') return;
		document.querySelectorAll(`.js-modal.${OPEN_CLASS}`).forEach(closeModal);
	});
};
